import {
	FormSwitch,
	FormUnit,
	type GetFormInput,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";
import { z } from "zod";

import { FormState as BarChartFormState } from "./bar-chart/form-state";
import { FormState as GaugeChartFormState } from "./gauge-chart/form-state";
import { FormState as StatChartFormState } from "./stat-chart/form-state";
import { FormState as TimeSeriesChartFormState } from "./time-series-chart/form-state";
import type { WidgetFormSync } from "./_sync";
import { ALL_KINDS, DEFAULT_KIND, type Kind, type Spec } from "./spec";

type FormState = FormSwitch<
	FormUnit<string, ReadonlyArray<string>, Kind>,
	{
		TimeSeriesChart: TimeSeriesChartFormState;
		BarChart: BarChartFormState;
		GaugeChart: GaugeChartFormState;
		StatChart: StatChartFormState;
	}
>;

const INITIAL: GetFormInput<FormState> = {
	active: DEFAULT_KIND,
	branches: {
		TimeSeriesChart: TimeSeriesChartFormState.initial,
		BarChart: BarChartFormState.initial,
		GaugeChart: GaugeChartFormState.initial,
		StatChart: StatChartFormState.initial,
	},
};

function fromSpec(spec: undefined | Spec) {
	return {
		active: spec?.kind ?? DEFAULT_KIND,
		branches: {
			TimeSeriesChart: TimeSeriesChartFormState.fromSettings(
				spec?.kind === "TimeSeriesChart" ? spec.settings : undefined,
			),
			BarChart: BarChartFormState.fromSettings(spec?.kind === "BarChart" ? spec.settings : undefined),
			GaugeChart: GaugeChartFormState.fromSettings(spec?.kind === "GaugeChart" ? spec.settings : undefined),
			StatChart: StatChartFormState.fromSettings(spec?.kind === "StatChart" ? spec.settings : undefined),
		},
	} satisfies GetFormInputSetter<FormState>;
}

type Input = ReturnType<typeof fromSpec>;

function init({ active, branches }: Input): FormState {
	return FormSwitch(
		FormUnit(active, {
			schema: z.enum(ALL_KINDS).catch(DEFAULT_KIND),
		}),
		{
			TimeSeriesChart: TimeSeriesChartFormState.init(branches.TimeSeriesChart),
			BarChart: BarChartFormState.init(branches.BarChart),
			GaugeChart: GaugeChartFormState.init(branches.GaugeChart),
			StatChart: StatChartFormState.init(branches.StatChart),
		},
	);
}

function toSpec({ active, branches }: GetFormOutputVerbose<FormState>): Spec {
	switch (active) {
		case "BarChart": {
			return {
				kind: active,
				settings: BarChartFormState.toSettings(branches.BarChart),
			};
		}

		case "StatChart": {
			return {
				kind: active,
				settings: StatChartFormState.toSettings(branches.StatChart),
			};
		}

		case "GaugeChart": {
			return {
				kind: active,
				settings: GaugeChartFormState.toSettings(branches.GaugeChart),
			};
		}

		default: {
			return {
				kind: DEFAULT_KIND,
				settings: TimeSeriesChartFormState.toSettings(branches.TimeSeriesChart),
			};
		}
	}
}

function toSync(kind: Kind, branches: Input["branches"]): WidgetFormSync {
	switch (kind) {
		case "TimeSeriesChart": {
			return TimeSeriesChartFormState.toSync(branches.TimeSeriesChart);
		}

		case "BarChart": {
			return BarChartFormState.toSync(branches.BarChart);
		}

		case "GaugeChart": {
			return GaugeChartFormState.toSync(branches.GaugeChart);
		}

		case "StatChart": {
			return StatChartFormState.toSync(branches.StatChart);
		}
	}
}

function fromSync(sync: WidgetFormSync): Input["branches"] {
	return {
		TimeSeriesChart: TimeSeriesChartFormState.fromSync(sync),
		BarChart: BarChartFormState.fromSync(sync),
		GaugeChart: GaugeChartFormState.fromSync(sync),
		StatChart: StatChartFormState.fromSync(sync),
	};
}

const FormState = {
	initial: INITIAL,
	fromSpec,
	init,
	toSpec,
	fromSync,
	toSync,
};

export { FormState };
